import { ThreeDRotation } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  StackProps,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useState } from "react";

type ListWithFilterProps<T extends Record<string, any>> = {
  items: T[];
  isLoading?: boolean;
  selectedItemId?: string;
  onSelected: (item: T) => void;
  placeholder: string;
  displayNameProperty: string;
  descriptionProperty?: string;
  keyProperty: string;
  groupProperty?: string;
  maxHeight?: string;
  emptyMessage?: string;
  icon?: (item: T) => React.ReactNode;
} & React.PropsWithChildren<StackProps>;

export default function ListWithFilter<T extends Record<string, any>>({
  items,
  isLoading,
  selectedItemId,
  onSelected,
  keyProperty,
  displayNameProperty,
  descriptionProperty,
  groupProperty,
  placeholder,
  maxHeight,
  emptyMessage,
  icon,
  ...props
}: ListWithFilterProps<T>) {
  const [nameFilter, setNameFilter] = useState("");

  const filteredExpressionMetadataList = React.useMemo(
    () => items.filter((x) => (x[displayNameProperty] as string).toLowerCase().includes(nameFilter.toLowerCase())),
    [nameFilter, items, displayNameProperty]
  );

  const groupedItems = React.useMemo(() => {
    if (!groupProperty) {
      return { "": filteredExpressionMetadataList };
    }

    return filteredExpressionMetadataList.reduce(
      (groups, item) => {
        const groupKey = (item[groupProperty] as string) || "Other";
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
        return groups;
      },
      {} as Record<string, T[]>
    );
  }, [filteredExpressionMetadataList, groupProperty]);

  const renderListItems = () => {
    if (!groupProperty) {
      // Render without groups
      return filteredExpressionMetadataList.map((x) => (
        <ListItem key={x[keyProperty] as string} disablePadding onClick={() => onSelected(x)}>
          <ListItemButton selected={selectedItemId === x[keyProperty]}>
            <ListItemIcon>{icon ? icon(x) : <ThreeDRotation />}</ListItemIcon>
            <ListItemText primary={x[displayNameProperty] as string} secondary={descriptionProperty ? (x[descriptionProperty] as string) : undefined} />
          </ListItemButton>
        </ListItem>
      ));
    }

    // Render with groups
    const groupKeys = Object.keys(groupedItems).sort();
    return groupKeys.map((groupKey) => (
      <React.Fragment key={groupKey}>
        {groupKey !== "" && (
          <ListItem>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "text.secondary", py: 1 }}>
              {groupKey}
            </Typography>
          </ListItem>
        )}
        {groupedItems[groupKey].map((x) => (
          <ListItem key={x[keyProperty] as string} disablePadding onClick={() => onSelected(x)} sx={{ pl: groupKey !== "" ? 2 : 0 }}>
            <ListItemButton selected={selectedItemId === x[keyProperty]}>
              <ListItemIcon>{icon ? icon(x) : <ThreeDRotation />}</ListItemIcon>
              <ListItemText primary={x[displayNameProperty] as string} secondary={descriptionProperty ? (x[descriptionProperty] as string) : undefined} />
            </ListItemButton>
          </ListItem>
        ))}
        {groupKey !== "" && groupKeys.indexOf(groupKey) < groupKeys.length - 1 && <Divider />}
      </React.Fragment>
    ));
  };

  return (
    <Stack direction="column" spacing={2} sx={{ flexGrow: 1, flexShrink: 1 }} {...props}>
      <Input placeholder={placeholder} value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} />
      <List sx={{ overflowY: "auto", flexGrow: 1, flexShrink: 1, maxHeight }}>
        {renderListItems()}
        {!isLoading && filteredExpressionMetadataList.length === 0 && <ListItem alignItems="center">{emptyMessage ?? "No items"}</ListItem>}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        )}
      </List>
    </Stack>
  );
}
